import axios from 'axios';

export const api = axios.create({
  baseURL: '/api',
  timeout: 8000,
});

export type Track = {
  id: number;
  title: string;
  year?: number | null;
  mp3_file?: string | null;
  cover_image?: string | null;
  duration_seconds?: number | null;
  order: number;
};
export type LiveEvent = {
  id: number;
  date: string;
  date_label?: string;
  city: string;
  venue: string;
  ticket_url?: string;
  status: string;
  notes?: string;
  order: number;
};

export type SiteSettings = {
  site_title: string;
  hero_tagline: string;
  short_bio: string;
  contact_email: string;
  hero_image?: string | null;
  empty_tracks_copy: string;
};

export type SocialLink = {
  platform: string;
  url: string;
  order: number;
};

export type Photo = {
  id: number;
  image: string;
  caption?: string;
  order: number;
  is_featured: boolean;
};

export async function fetchSettings() {
  const res = await api.get<SiteSettings[]>('/settings/');
  return res.data[0] ?? null;
}

export async function fetchSocialLinks() {
  const res = await api.get<SocialLink[]>('/social-links/');
  return res.data;
}

export async function fetchPhotos() {
  const res = await api.get<Photo[]>('/photos/');
  return res.data;
}

export async function fetchTracks() {
  const res = await api.get<Track[]>('/tracks/');
  return res.data;
}

export async function fetchLiveEvents() {
  const res = await api.get<LiveEvent[]>('/live-events/');
  return res.data;
}


// === Auth + Admin (con CSRF e cookie di sessione) ===

api.defaults.withCredentials = true;
api.defaults.xsrfCookieName = 'csrftoken';
api.defaults.xsrfHeaderName = 'X-CSRFToken';

export type Me = {
  authenticated: boolean;
  id?: number;
  username?: string;
  is_staff?: boolean;
  is_superuser?: boolean;
  member_id?: number | null;
  display_name?: string;
};

export type BandMember = {
  id: number;
  display_name: string;
  instrument?: string;
  color: string;
  order: number;
  is_active: boolean;
};

export type CalendarKind = 'concert' | 'rehearsal' | 'unavailable' | 'other';

export type CalendarEvent = {
  id: number;
  kind: CalendarKind;
  title: string;
  notes?: string;
  start: string;
  end?: string | null;
  all_day: boolean;
  location?: string;
  member_ids: number[];
  live_event?: number | null;
  created_at?: string;
  updated_at?: string;
};

export async function ensureCsrf() {
  await api.get('/auth/csrf/');
}

export async function login(username: string, password: string) {
  await ensureCsrf();
  const res = await api.post('/auth/login/', { username, password });
  return res.data as Me;
}

export async function logout() {
  await ensureCsrf();
  await api.post('/auth/logout/', {});
}

export async function fetchMe() {
  const res = await api.get<Me>('/auth/me/');
  return res.data;
}

// --- Admin CRUD ---

export type AdminLiveEvent = LiveEvent & { is_active: boolean; order: number };
export type AdminPhoto = Photo & { is_active: boolean };
export type AdminTrack = Track & { is_active: boolean };

export const adminApi = {
  // Live events
  liveList: () => api.get<AdminLiveEvent[]>('/admin/live-events/').then((r) => r.data),
  liveCreate: (data: Partial<AdminLiveEvent>) => api.post<AdminLiveEvent>('/admin/live-events/', data).then((r) => r.data),
  liveUpdate: (id: number, data: Partial<AdminLiveEvent>) => api.patch<AdminLiveEvent>(`/admin/live-events/${id}/`, data).then((r) => r.data),
  liveDelete: (id: number) => api.delete(`/admin/live-events/${id}/`),
  liveReorder: (order: number[]) => api.post('/admin/live-events/reorder/', { order }),

  // Photos (multipart per upload)
  photoList: () => api.get<AdminPhoto[]>('/admin/photos/').then((r) => r.data),
  photoCreate: (formData: FormData) => api.post<AdminPhoto>('/admin/photos/', formData).then((r) => r.data),
  photoUpdate: (id: number, data: Partial<AdminPhoto> | FormData) => api.patch<AdminPhoto>(`/admin/photos/${id}/`, data).then((r) => r.data),
  photoDelete: (id: number) => api.delete(`/admin/photos/${id}/`),
  photoReorder: (order: number[]) => api.post('/admin/photos/reorder/', { order }),

  // Tracks
  trackList: () => api.get<AdminTrack[]>('/admin/tracks/').then((r) => r.data),
  trackCreate: (formData: FormData) => api.post<AdminTrack>('/admin/tracks/', formData).then((r) => r.data),
  trackUpdate: (id: number, data: Partial<AdminTrack> | FormData) => api.patch<AdminTrack>(`/admin/tracks/${id}/`, data).then((r) => r.data),
  trackDelete: (id: number) => api.delete(`/admin/tracks/${id}/`),
  trackReorder: (order: number[]) => api.post('/admin/tracks/reorder/', { order }),

  // Members
  memberList: () => api.get<BandMember[]>('/admin/members/').then((r) => r.data),

  // Calendar
  calendarList: (params?: { start?: string; end?: string }) =>
    api.get<CalendarEvent[]>('/admin/calendar/', { params }).then((r) => r.data),
  calendarCreate: (data: Partial<CalendarEvent>) => api.post<CalendarEvent>('/admin/calendar/', data).then((r) => r.data),
  calendarUpdate: (id: number, data: Partial<CalendarEvent>) => api.patch<CalendarEvent>(`/admin/calendar/${id}/`, data).then((r) => r.data),
  calendarDelete: (id: number) => api.delete(`/admin/calendar/${id}/`),
};
